/**
 * Preprocess an image file client-side before upload.
 *
 * For profile pictures: square center-crop, max 800x800, JPEG quality 0.8
 * For activity attachments: proportional resize, max 1200px longest side, JPEG quality 0.8
 *
 * @param {File} file - The original image file
 * @param {Object} options - Processing options
 * @param {string} [options.purpose='attachment'] - 'profile' or 'attachment'
 * @param {number} [options.maxDimension=800] - Max dimension (profile: square crop, attachment: proportional)
 * @param {number} [options.quality=0.8] - JPEG quality (0-1)
 * @returns {Promise<File>} - A new File object with the processed image
 */
export async function preprocessImage(file, { purpose = 'attachment', maxDimension = 800, quality = 0.8 } = {}) {
    // Fast path: skip preprocessing for small files — decode overhead isn't worth it
    const SKIP_THRESHOLD = 500 * 1024; // 500 KB
    if (file.size < SKIP_THRESHOLD) {
        return Promise.resolve(file);
    }

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                let sourceX = 0, sourceY = 0, sourceW = img.width, sourceH = img.height;
                let canvasW, canvasH;

                if (purpose === 'profile') {
                    // Square center-crop
                    const size = Math.min(img.width, img.height);
                    sourceX = (img.width - size) / 2;
                    sourceY = (img.height - size) / 2;
                    sourceW = size;
                    sourceH = size;

                    if (size > maxDimension) {
                        canvasW = maxDimension;
                        canvasH = maxDimension;
                    } else {
                        canvasW = size;
                        canvasH = size;
                    }
                } else {
                    // Proportional resize — max 1200px on longest side
                    if (img.width > maxDimension || img.height > maxDimension) {
                        const ratio = Math.min(maxDimension / img.width, maxDimension / img.height);
                        canvasW = Math.round(img.width * ratio);
                        canvasH = Math.round(img.height * ratio);
                    } else {
                        canvasW = img.width;
                        canvasH = img.height;
                    }
                }

                canvas.width = canvasW;
                canvas.height = canvasH;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, sourceX, sourceY, sourceW, sourceH, 0, 0, canvasW, canvasH);

                canvas.toBlob(blob => {
                    if (blob) {
                        const extName = file.name.replace(/\.[^.]+$/, '.jpg');
                        resolve(new File([blob], extName, { type: 'image/jpeg' }));
                    } else {
                        reject(new Error('Canvas toBlob returned null'));
                    }
                }, 'image/jpeg', quality);
            } catch (err) {
                reject(err);
            }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(file);
    });
}
